import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { invalidateIfNotInFlight } from '@/patients/lib/invalidate-if-not-in-flight';
import type { RevokeConsentBody } from '@/consent/types/consent-api';

/**
 * POST /api/Consent/RevokeConsent → boolean.
 *
 * Contract: Scrips.Patient `ConsentController.RevokeConsent` /
 * `RevokeConsentRequest` (PR #882). Sets status → inactive and appends a
 * revocation row to the audit trail (FHIR Provenance). `revocationReason` is
 * required — the UI gates Save until a reason is entered (BRD UC-CONSENT
 * revocation flow). A destructive action — guard with shadcn `AlertDialog`,
 * never `window.confirm`.
 *
 * For an `hie-opt-out` consent this also drives the NABIDH opt-out leg
 * server-side (CG-B5) — a separate revocation path from clinical consent.
 *
 * Cache: invalidates the record, the patient list, and the audit trail.
 */
export function useRevokeConsent(patientId?: string | null) {
  const qc = useQueryClient();
  return useMutation<boolean, Error, RevokeConsentBody>({
    mutationFn: (body) => post<boolean>('/api/Consent/RevokeConsent', body),
    onSuccess: (_ok, { consentRecordId }) => {
      invalidateIfNotInFlight(qc, ['consent', consentRecordId]);
      invalidateIfNotInFlight(qc, ['consent-audit', consentRecordId]);
      if (patientId) invalidateIfNotInFlight(qc, ['patient-consents', patientId]);
    },
  });
}
